import type { Actor, Command, Player, Tournament } from "@spikeeasy/tournament";

export interface PlayerProfile extends Player {
  tournamentCount: number;
  /** Present only for admins. */
  accountEmail?: string | null;
}

export type Role = "admin" | "coach" | "player";
export interface Account {
  id: string;
  email: string;
  name: string | null;
  roles: Role[];
  playerId: string | null;
}
export interface Session {
  actor: Actor | null;
  account: Account | null;
  /** Present when live sign-in emails need a Turnstile check. */
  turnstileSiteKey?: string | null;
}
export const hasRole = (account: Account | null, role: Role) =>
  !!account?.roles.includes(role);

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
  }
}
export async function api<T>(
  path: string,
  value?: unknown,
  method = "POST",
): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: "same-origin",
    signal: AbortSignal.timeout(6000),
    ...(value === undefined
      ? {}
      : {
          method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(value),
        }),
  });
  const result = (await response.json()) as { error?: string; code?: string };
  if (!response.ok)
    throw new ApiError(
      result.error ?? "The request failed. Please retry.",
      response.status,
      result.code,
    );
  return result as T;
}
export interface Snapshot {
  tournament: Tournament;
  serverTime: string;
  /** How this browser may act in this tournament, and the key its queued commands use. */
  actor?: Actor | null;
  actorKey?: string | null;
}
export interface PendingCommand {
  id: string;
  tournamentId: string;
  actor: string;
  version: number;
  command: Command;
  createdAt: number;
  error?: string;
  errorStatus?: number;
}
export const actorId = (actor: Actor | null, key?: string | null) =>
  key ??
  (!actor
    ? "public"
    : actor.role === "admin"
      ? "admin"
      : `${actor.tournamentId}:${actor.teamId}`);
